import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { WarningAmber as WarningAmberIcon, Close as CloseIcon } from '@mui/icons-material'

export interface CsvWarningFlags {
  has_duplicate_phone?: boolean
  missing_template_data?: boolean
}

interface CsvValidationWarningDialogProps {
  open: boolean
  onClose: () => void
  onDownloadReport: () => void
  onContinueAnyway: () => void
  invalidCsvUrl: string | null
  warnings: CsvWarningFlags
}

const WARNING_MESSAGES: { key: keyof CsvWarningFlags; message: React.ReactNode }[] = [
  {
    key: 'has_duplicate_phone',
    message: (
      <>
        <strong>Duplicate phone numbers:</strong> The same phone number appears in multiple rows.
        Leads may receive multiple messages.
      </>
    ),
  },
  {
    key: 'missing_template_data',
    message: (
      <>
        <strong>Missing template data:</strong> Some template-mapped cells are empty. Messages may be
        sent with missing information.
      </>
    ),
  },
]

const CsvValidationWarningDialog: React.FC<CsvValidationWarningDialogProps> = ({
  open,
  onClose,
  onDownloadReport,
  onContinueAnyway,
  invalidCsvUrl,
  warnings,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const activeWarnings = WARNING_MESSAGES.filter(({ key }) => warnings[key])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          ...(isMobile ? { m: 0, width: '95%', maxWidth: '100%', borderRadius: 2 } : {}),
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 0.5, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          Warnings found in your CSV
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 12,
            top: 10,
            color: 'text.secondary',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={isMobile ? { p: 2, pt: 1 } : {}}>
        <DialogContentText sx={{ mb: 2, fontSize: isMobile ? '0.875rem' : 'inherit' }}>
          Some rows have warnings that may affect message delivery. Download the report to find the
          affected rows, or continue anyway.
        </DialogContentText>

        <Box
          component="ul"
          sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 1.5 }}
        >
          {activeWarnings.map(({ key, message }) => (
            <Box component="li" key={key}>
              <DialogContentText
                sx={{
                  color: 'text.primary',
                  fontSize: isMobile ? '0.875rem' : 'inherit',
                  wordBreak: 'break-word',
                }}
              >
                {message}
              </DialogContentText>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 1.5,
          '& > :not(:first-of-type)': {
            ml: 0,
          },
        }}
      >
        {invalidCsvUrl && (
          <Button
            fullWidth={isMobile}
            variant="outlined"
            color="primary"
            onClick={onDownloadReport}
            sx={{ py: isMobile ? 1.2 : 1, fontWeight: 600, width: isMobile ? '100%' : 'auto' }}
          >
            Download Report
          </Button>
        )}
        <Button
          fullWidth={isMobile}
          variant="contained"
          onClick={onContinueAnyway}
          sx={{ py: isMobile ? 1.2 : 1, fontWeight: 600, width: isMobile ? '100%' : 'auto' }}
        >
          Continue Anyway
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CsvValidationWarningDialog
